import type { Table as TanstackTable } from "@tanstack/react-table";
import { Button } from "components/ui/Button/Button";

type DataTablePaginationProps<T> = {
  table: TanstackTable<T>;
  pageSizeOptions: number[];
  showSizeChanger: boolean;
  showTotal: boolean;
  totalCount?: number;
};

/**
 * DataTable 用ページネーションバー。TanStack Table のインスタンスを受け取り、
 * prev / next / pageSize / total を描画する。
 * @param {DataTablePaginationProps} props ページネーション props
 * @return {JSX.Element} ページネーション UI
 */
export const DataTablePagination = <T,>({
  table,
  pageSizeOptions,
  showSizeChanger,
  showTotal,
  totalCount,
}: DataTablePaginationProps<T>): JSX.Element => {
  const pageIndex = table.getState().pagination.pageIndex;
  const pageSize = table.getState().pagination.pageSize;
  const pageCount = table.getPageCount();
  const total = totalCount ?? table.getFilteredRowModel().rows.length;
  const rangeStart = total === 0 ? 0 : pageIndex * pageSize + 1;
  const rangeEnd = Math.min((pageIndex + 1) * pageSize, total);

  return (
    <div className="flex items-center justify-between gap-4 px-1 py-3 text-sm">
      <div className="flex items-center gap-2 text-base-content/70">
        {showTotal && (
          <span>
            {rangeStart}–{rangeEnd} / {total} 件
          </span>
        )}
        {showSizeChanger && (
          <label className="flex items-center gap-1 ml-4">
            <span className="text-xs">表示件数:</span>
            <select
              className="select select-bordered select-xs"
              value={pageSize}
              onChange={e => table.setPageSize(Number(e.target.value))}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="xs"
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
        >
          «
        </Button>
        <Button
          variant="outline"
          size="xs"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          ‹ 前へ
        </Button>
        <span className="text-xs">
          {pageIndex + 1} / {Math.max(pageCount, 1)}
        </span>
        <Button
          variant="outline"
          size="xs"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          次へ ›
        </Button>
        <Button
          variant="outline"
          size="xs"
          onClick={() => table.setPageIndex(pageCount - 1)}
          disabled={!table.getCanNextPage()}
        >
          »
        </Button>
      </div>
    </div>
  );
};
